/**
 * Anonymous public unit detail route — /public/offers/:token/units/:unitId
 *
 * Fetches the anonymous token-scoped detail endpoint
 * (`publicOfferLink.publicUnitDetail`) via the typed client (no auth cookies —
 * `defaultCredentials: 'omit'`) and renders the shared `MediaUnitDetailView`
 * with the redacted client payload in client mode. Renders outside the SPA
 * root authentication guard and outside `AppShell` (handled by `__root.tsx`'s
 * `/public/*` allow-list). Shows the loading skeleton during fetch, the
 * shared "link invalid / not found" state on 404, and the shared "link
 * expired" state on 410 — the same states the public offer page uses.
 *
 * Traceability: client-media-unit-detail / Public client media-unit detail route.
 */
import * as React from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isExpiredLinkError, isNotFoundError } from '@ooh/contracts';
import { ClientDetailBackLink } from '../../../features/media-units/components/client-detail-back-link';
import {
  DetailSkeleton,
  MediaUnitDetailView,
  PublicOfferExpiredPage,
  PublicOfferInvalidPage,
} from '@ooh/screens';
import { PageTitleContext } from '../../__root';
import { usePublicUnitDetail } from '../../../features/offers/public-offer/public-offer-queries';

export const Route = createFileRoute('/public/offers/$token/units/$unitId')({
  component: PublicUnitDetailPage,
});

export function PublicUnitDetailPage() {
  const { t } = useTranslation('inventory');
  const { token, unitId } = useParams({ from: '/public/offers/$token/units/$unitId' });
  const { setPageTitle } = React.useContext(PageTitleContext);

  const { data, isLoading, isError, error } = usePublicUnitDetail(token, unitId);

  if (isLoading) return <DetailSkeleton />;
  if (isError) {
    if (isExpiredLinkError(error)) return <PublicOfferExpiredPage />;
    if (isNotFoundError(error)) return <PublicOfferInvalidPage />;
    throw error;
  }
  if (!data) return null;

  return (
    <MediaUnitDetailView
      unit={data.data}
      canViewCosts={false}
      clientView
      onPageTitleChange={setPageTitle}
      backLink={
        <ClientDetailBackLink
          to="/public/offers/$token"
          params={{ token }}
          label={t('detail.clientDetail.backToOffer')}
        />
      }
    />
  );
}
